import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  Undo2,
  Wallet,
  Ban,
  Trash2,
  AlertTriangle,
  Loader2,
  Pencil,
  Plus,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ESTADOS, TIPOS_NOMINA, fechaCorta, pesos } from '../nomina/utils/nomina';
import {
  getNominaDirecta,
  recalcularNominaDirecta,
  actualizarDistribucion,
  actualizarLineaDirecta,
  cambiarEstadoNominaDirecta,
  eliminarNominaDirecta,
  METODOS_DISTRIBUCION,
} from './Services/nomina-directa.services';
import ParticipacionCentros from './components/ParticipacionCentros';
import { getSedesNomina } from '../nomina/Services/nomina.services';

const pct = (v) => `${((Number(v) || 0) * 100).toLocaleString('es-CO', { maximumFractionDigits: 2 })} %`;
const kilos = (v) => `${(Number(v) || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 })} kg`;

const Stat = ({ titulo, valor, detalle, destacado }) => (
  <div className={`rounded-lg border p-3 ${destacado ? 'bg-primary/5 border-primary/30' : 'bg-card'}`}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{titulo}</div>
    <div className="text-lg font-semibold tabular-nums">{valor}</div>
    {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
  </div>
);

const NominaDirectaDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const usuario = { id: user?._id || user?.id, nombre: user?.nombre || user?.name || user?.email };

  const [nomina, setNomina] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trabajando, setTrabajando] = useState('');
  const [confirmar, setConfirmar] = useState(null); // 'anular' | 'eliminar'
  const [motivo, setMotivo] = useState('');
  const [editando, setEditando] = useState(null);
  const [centroAbierto, setCentroAbierto] = useState(null);
  const [formLinea, setFormLinea] = useState({
    salario: '',
    diasLaborados: '',
    ajustes: [],
    observaciones: '',
    metodo: 'equitativa',
    centros: [],
    personalizada: false,
  });
  const [sedes, setSedes] = useState([]);

  const cargar = async () => {
    try {
      setNomina(await getNominaDirecta(id));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [id]);

  useEffect(() => {
    getSedesNomina().then(setSedes).catch(() => setSedes([]));
  }, []);

  const accion = async (nombre, fn, exito) => {
    setTrabajando(nombre);
    try {
      const r = await fn();
      if (r && r._id) setNomina(r);
      if (exito) toast({ title: exito });
      return true;
    } catch (e) {
      toast({ title: 'No se pudo completar', description: e.message, variant: 'destructive' });
      return false;
    } finally {
      setTrabajando('');
    }
  };

  // De dónde sale el costo de un centro: la parte que le carga cada persona, con el
  // método y la participación con que se repartió.
  const aportesDelCentro = (centroId) =>
    (nomina?.lineas || [])
      .map((l) => {
        const parte = (l.centros || []).find((c) => String(c.id) === String(centroId));
        return parte ? { linea: l, parte } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.parte.costoEmpresa - a.parte.costoEmpresa);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Cargando nómina directa…</div>;
  }
  if (!nomina) {
    return (
      <div className="p-8 text-center space-y-3">
        <p className="text-muted-foreground">No se encontró la nómina directa.</p>
        <Button variant="outline" onClick={() => navigate('/personal/nomina-directa')}>Volver</Button>
      </div>
    );
  }

  const t = nomina.totales || {};
  const enBorrador = nomina.estado === 'borrador';

  const abrirLinea = (l) => {
    setEditando(l);
    setFormLinea({
      salario: l.salarioPersonalizado ? String(l.salario) : '',
      diasLaborados: l.diasPersonalizados ? String(l.diasLaborados) : '',
      ajustes: (l.ajustes || []).map((a) => ({ ...a, valor: String(a.valor) })),
      observaciones: l.observaciones || '',
      metodo: l.metodoDistribucion || 'equitativa',
      centros: (l.centros || []).map((c) => ({
        id: c.id,
        nombre: c.nombre || '',
        porcentaje: c.porcentaje ?? '',
      })),
      personalizada: !!l.distribucionPersonalizada,
    });
  };

  const guardarLinea = async () => {
    const ok = await accion(
      'linea',
      () =>
        actualizarLineaDirecta(id, editando._id, {
          salario: formLinea.salario === '' ? null : Number(formLinea.salario),
          diasLaborados: formLinea.diasLaborados === '' ? null : Number(formLinea.diasLaborados),
          ajustes: formLinea.ajustes
            .filter((a) => Number(a.valor))
            .map((a) => ({ concepto: a.concepto, valor: Number(a.valor), naturaleza: a.naturaleza })),
          observaciones: formLinea.observaciones,
          ...(formLinea.personalizada
            ? {
                metodoDistribucion: formLinea.metodo,
                centros: formLinea.centros.map((c) => ({
                  id: c.id,
                  nombre: c.nombre,
                  porcentaje: formLinea.metodo === 'porcentaje' ? Number(c.porcentaje) || 0 : null,
                })),
              }
            : { distribucionPersonalizada: false }),
        }),
      'Persona recalculada',
    );
    if (ok) setEditando(null);
  };

  // Solo cambia el reparto POR DEFECTO (el de las personas sin participación propia en su
  // ficha); el backend no toca las líneas ajustadas a mano.
  const cambiarMetodoDefecto = (metodo) =>
    accion('distribucion', () => actualizarDistribucion(id, { metodoDistribucion: metodo }), 'Reparto por defecto actualizado');

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        {/* Encabezado */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/personal/nomina-directa')} title="Volver">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{nomina.nombre}</h2>
                <Badge variant={ESTADOS[nomina.estado]?.variant}>{ESTADOS[nomina.estado]?.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {TIPOS_NOMINA[nomina.tipo]} · {fechaCorta(nomina.desde)} – {fechaCorta(nomina.hasta)} ·{' '}
                {METODOS_DISTRIBUCION[nomina.metodoDistribucion]} · {(nomina.centros || []).length} centros
              </p>
              {nomina.calculadaEn && (
                <p className="text-xs text-muted-foreground">
                  Calculada {new Date(nomina.calculadaEn).toLocaleString('es-CO')}
                  {nomina.aprobadoPor?.fecha && ` · Aprobada por ${nomina.aprobadoPor.nombre || '—'}`}
                  {nomina.pagadoPor?.fecha && ' · Pagada'}
                  {nomina.anuladoPor?.fecha && ` · Anulada: ${nomina.anuladoPor.motivo || ''}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {enBorrador && (
              <>
                <Button
                  variant="outline"
                  disabled={!!trabajando}
                  onClick={() => accion('recalcular', () => recalcularNominaDirecta(id), 'Nómina recalculada')}
                >
                  {trabajando === 'recalcular' ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Recalcular
                </Button>
                <Button
                  disabled={!!trabajando}
                  onClick={() =>
                    accion('aprobar', () => cambiarEstadoNominaDirecta(id, 'aprobar', { usuario }), 'Nómina aprobada')
                  }
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Aprobar
                </Button>
              </>
            )}
            {nomina.estado === 'aprobada' && (
              <>
                <Button
                  variant="outline"
                  disabled={!!trabajando}
                  onClick={() => accion('reabrir', () => cambiarEstadoNominaDirecta(id, 'reabrir'), 'Devuelta a borrador')}
                >
                  <Undo2 className="mr-2 h-4 w-4" /> A borrador
                </Button>
                <Button
                  disabled={!!trabajando}
                  onClick={() => accion('pagar', () => cambiarEstadoNominaDirecta(id, 'pagar', { usuario }), 'Marcada como pagada')}
                >
                  <Wallet className="mr-2 h-4 w-4" /> Marcar pagada
                </Button>
              </>
            )}
            {nomina.estado !== 'anulada' && (
              <Button
                variant="outline"
                className="text-destructive"
                disabled={!!trabajando}
                onClick={() => setConfirmar('anular')}
              >
                <Ban className="mr-2 h-4 w-4" /> Anular
              </Button>
            )}
            {enBorrador && (
              <Button
                variant="ghost"
                className="text-destructive"
                disabled={!!trabajando}
                onClick={() => setConfirmar('eliminar')}
                title="Eliminar borrador"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {(nomina.advertencias || []).length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <ul className="space-y-1">
              {nomina.advertencias.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Totales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat titulo="Personas" valor={t.personas ?? 0} detalle="Personal que no marca en planta" />
          <Stat titulo="Básico" valor={pesos(t.basico)} detalle={`Aux. transporte ${pesos(t.auxilioTransporte)}`} />
          <Stat titulo="Devengado" valor={pesos(t.devengado)} detalle={`Deducciones ${pesos(t.deducciones)}`} />
          <Stat titulo="Neto a pagar" valor={pesos(t.neto)} />
          <Stat titulo="Aportes empleador" valor={pesos(t.aportes)} />
          <Stat titulo="Prestaciones" valor={pesos(t.prestaciones)} detalle="Provisión del periodo" />
          <Stat titulo="Costo total empresa" valor={pesos(t.costoEmpresa)} destacado detalle="Es lo que se reparte" />
        </div>

        {/* Reparto por centro */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Reparto por centro de lavado</h3>
              <p className="text-xs text-muted-foreground">
                Consolidado de la participación de cada persona, congelado al calcular: queda como evidencia de
                con qué se repartió. Pulsa un centro para ver de dónde sale su costo.
              </p>
            </div>
            {enBorrador && (
              <div className="flex items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-xs" title="Solo para las personas sin participación propia en su ficha">
                    Reparto por defecto
                  </Label>
                  <Select value={nomina.metodoDistribucion} onValueChange={cambiarMetodoDefecto} disabled={!!trabajando}>
                    <SelectTrigger className="w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(METODOS_DISTRIBUCION).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Centro</TableHead>
                  <TableHead className="text-right">Personas</TableHead>
                  <TableHead className="text-right">Kilos del periodo</TableHead>
                  <TableHead className="text-right" title="Parte del costo total de esta nómina">Participación</TableHead>
                  <TableHead className="text-right">Devengado</TableHead>
                  <TableHead className="text-right">Neto</TableHead>
                  <TableHead className="text-right">Costo empresa</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(nomina.centros || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      Ninguna persona tiene centros asignados: revisa su participación.
                    </TableCell>
                  </TableRow>
                ) : (
                  (nomina.centros || []).map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => setCentroAbierto(c)}
                      title="Ver de dónde sale este costo"
                    >
                      <TableCell className="font-medium whitespace-nowrap">{c.nombre || c.id}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.personas ?? 0}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">{kilos(c.kilos)}</TableCell>
                      <TableCell className="text-right tabular-nums">{pct(c.factor)}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(c.devengado)}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(c.neto)}</TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                        {pesos(c.costoEmpresa)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Personas */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Personas ({(nomina.lineas || []).length})</h3>
          <div className="rounded-lg border bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Reparto</TableHead>
                  <TableHead className="text-right">Salario</TableHead>
                  <TableHead className="text-right">Días</TableHead>
                  <TableHead className="text-right">Básico</TableHead>
                  <TableHead className="text-right">Aux. transp.</TableHead>
                  <TableHead className="text-right">Ajustes</TableHead>
                  <TableHead className="text-right">Devengado</TableHead>
                  <TableHead className="text-right">Deduc.</TableHead>
                  <TableHead className="text-right">Neto</TableHead>
                  <TableHead className="text-right">Costo empresa</TableHead>
                  {enBorrador && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(nomina.lineas || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-muted-foreground">Sin personas.</TableCell>
                  </TableRow>
                ) : (
                  nomina.lineas.map((l) => {
                    const ajustes = (l.ajustes || []).reduce(
                      (s, a) => s + (a.naturaleza === 'deduccion' ? -1 : 1) * (Number(a.valor) || 0),
                      0,
                    );
                    return (
                      <TableRow key={l._id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="font-medium flex items-center gap-1">
                            {l.persona?.nombre}
                            {(l.advertencias || []).length > 0 && (
                              <AlertTriangle
                                className="h-3.5 w-3.5 text-amber-500"
                                title={(l.advertencias || []).join('\n')}
                              />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{l.persona?.cedula || 'Sin documento'}</div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{l.persona?.cargo || '—'}</TableCell>
                        <TableCell
                          className="whitespace-nowrap text-sm"
                          title={(l.centros || [])
                            .map(
                              (c) =>
                                `${c.nombre || c.id}: ${pct(c.factor)}${
                                  l.metodoDistribucion === 'porcentaje' && c.porcentaje != null ? ` (${c.porcentaje} %)` : ''
                                }${l.metodoDistribucion === 'kilos' ? ` · ${kilos(c.kilos)}` : ''}`,
                            )
                            .join('\n')}
                        >
                          {METODOS_DISTRIBUCION[l.metodoDistribucion] || l.metodoDistribucion}
                          <div className="text-xs text-muted-foreground">
                            {(l.centros || []).length} centro{(l.centros || []).length === 1 ? '' : 's'}
                            {l.distribucionPersonalizada && ' · ajustado aquí'}
                          </div>
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums whitespace-nowrap ${l.salarioPersonalizado ? 'text-primary font-medium' : ''}`}
                          title={l.salarioPersonalizado ? 'Salario fijado a mano' : undefined}
                        >
                          {pesos(l.salario)}
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${l.diasPersonalizados ? 'text-primary font-medium' : ''}`}
                          title={l.diasPersonalizados ? 'Días fijados a mano' : undefined}
                        >
                          {l.diasLaborados}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(l.valores?.basico)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {pesos(l.valores?.auxilioTransporte)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {ajustes ? pesos(ajustes) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(l.totalDevengado)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(l.totalDeducciones)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">{pesos(l.neto)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {pesos(l.costoEmpresa?.total)}
                        </TableCell>
                        {enBorrador && (
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" title="Editar" onClick={() => abrirLinea(l)}>
                              <Pencil className="h-4 w-4 text-green-600" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Edición de una línea. El modal va ancho y a dos columnas: a la izquierda lo que se
          liquida, a la derecha el reparto y el costo que le queda a cada centro, que es lo
          que se está decidiendo al tocar la participación. */}
      <Dialog open={!!editando} onOpenChange={(v) => !v && setEditando(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle>{editando?.persona?.nombre}</DialogTitle>
            <DialogDescription>
              {[editando?.persona?.cedula, editando?.persona?.cargo, editando?.persona?.area]
                .filter(Boolean)
                .join(' · ') || 'Sin datos de la ficha'}
              {' · '}
              Deja un campo vacío para volver a lo que diga la ficha.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
              {/* Liquidación */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold">Liquidación del periodo</h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="nd-salario">Salario del periodo</Label>
                    <Input
                      id="nd-salario"
                      type="number"
                      min="0"
                      placeholder={`Ficha: ${editando?.salario ?? 0}`}
                      value={formLinea.salario}
                      onChange={(e) => setFormLinea({ ...formLinea, salario: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nd-dias">Días laborados</Label>
                    <Input
                      id="nd-dias"
                      type="number"
                      min="0"
                      max="31"
                      placeholder={`Calculado: ${editando?.diasLaborados ?? 0}`}
                      value={formLinea.diasLaborados}
                      onChange={(e) => setFormLinea({ ...formLinea, diasLaborados: e.target.value })}
                    />
                  </div>
                </div>

                <div className="rounded-md border bg-card p-3 text-sm space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Salario mensual</span>
                    <span className="tabular-nums">{pesos(editando?.salario)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Básico ({editando?.diasLaborados ?? 0} días)</span>
                    <span className="tabular-nums">{pesos(editando?.valores?.basico)}</span>
                  </div>
                  {editando?.valores?.auxilioTransporte > 0 && (
                    <div className="flex justify-between">
                      <span>Auxilio de transporte</span>
                      <span className="tabular-nums">{pesos(editando.valores.auxilioTransporte)}</span>
                    </div>
                  )}
                  {(editando?.conceptos || []).length > 0 && (
                    <div className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Conceptos
                    </div>
                  )}
                  {(editando?.conceptos || []).map((c, i) => (
                    <div key={i} className="flex justify-between">
                      <span>
                        {c.naturaleza === 'deduccion' ? '(−) ' : '(+) '}
                        {c.nombre}
                        {c.tipoCalculo === 'porcentaje' ? ` ${c.valor} %` : ''}
                      </span>
                      <span className="tabular-nums">{pesos(c.montoCalculado)}</span>
                    </div>
                  ))}
                  {(editando?.ajustes || []).map((a, i) => (
                    <div key={`a${i}`} className="flex justify-between text-primary">
                      <span>
                        {a.naturaleza === 'deduccion' ? '(−) ' : '(+) '}
                        {a.concepto}
                      </span>
                      <span className="tabular-nums">{pesos(a.valor)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t pt-1">
                    <span>Devengado</span>
                    <span className="tabular-nums">{pesos(editando?.totalDevengado)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Deducciones</span>
                    <span className="tabular-nums">− {pesos(editando?.totalDeducciones)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Neto</span>
                    <span className="tabular-nums">{pesos(editando?.neto)}</span>
                  </div>

                  {editando?.costoEmpresa && (
                    <div className="border-t pt-1 mt-1 space-y-0.5">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Costo empresa (no se descuenta a la persona)
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Devengado</span>
                        <span className="tabular-nums">{pesos(editando.totalDevengado)}</span>
                      </div>
                      {[
                        ['Pensión', editando.costoEmpresa.aportes?.pension],
                        ['ARL', editando.costoEmpresa.aportes?.arl],
                        ['Caja de compensación', editando.costoEmpresa.aportes?.caja],
                        ['Salud empleador', editando.costoEmpresa.aportes?.salud],
                        ['ICBF', editando.costoEmpresa.aportes?.icbf],
                        ['SENA', editando.costoEmpresa.aportes?.sena],
                        ['Prima (provisión)', editando.costoEmpresa.prestaciones?.prima],
                        ['Cesantías (provisión)', editando.costoEmpresa.prestaciones?.cesantias],
                        ['Intereses de cesantías', editando.costoEmpresa.prestaciones?.interesesCesantias],
                        ['Vacaciones (provisión)', editando.costoEmpresa.prestaciones?.vacaciones],
                      ]
                        .filter(([, v]) => v)
                        .map(([nombre, v]) => (
                          <div key={nombre} className="flex justify-between text-muted-foreground">
                            <span>{nombre}</span>
                            <span className="tabular-nums">{pesos(v)}</span>
                          </div>
                        ))}
                      <div className="flex justify-between font-semibold">
                        <span>Costo total empresa</span>
                        <span className="tabular-nums">{pesos(editando.costoEmpresa.total)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground pt-1">
                        Es el costo total que se reparte entre los centros.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Ajustes</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFormLinea((f) => ({
                          ...f,
                          ajustes: [...f.ajustes, { concepto: '', valor: '', naturaleza: 'devengado' }],
                        }))
                      }
                    >
                      <Plus className="mr-1 h-3 w-3" /> Agregar
                    </Button>
                  </div>
                  {formLinea.ajustes.length === 0 && (
                    <p className="text-xs text-muted-foreground">Sin ajustes en este periodo.</p>
                  )}
                  {formLinea.ajustes.map((a, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        className="flex-1 min-w-0"
                        placeholder="Concepto"
                        value={a.concepto}
                        onChange={(e) =>
                          setFormLinea((f) => ({
                            ...f,
                            ajustes: f.ajustes.map((x, j) => (j === i ? { ...x, concepto: e.target.value } : x)),
                          }))
                        }
                      />
                      <Input
                        className="w-28 shrink-0"
                        type="number"
                        min="0"
                        placeholder="Valor"
                        value={a.valor}
                        onChange={(e) =>
                          setFormLinea((f) => ({
                            ...f,
                            ajustes: f.ajustes.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)),
                          }))
                        }
                      />
                      <Select
                        value={a.naturaleza}
                        onValueChange={(v) =>
                          setFormLinea((f) => ({
                            ...f,
                            ajustes: f.ajustes.map((x, j) => (j === i ? { ...x, naturaleza: v } : x)),
                          }))
                        }
                      >
                        <SelectTrigger className="w-32 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="devengado">Devengado</SelectItem>
                          <SelectItem value="deduccion">Deducción</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => setFormLinea((f) => ({ ...f, ajustes: f.ajustes.filter((_, j) => j !== i) }))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nd-obs">Observaciones</Label>
                  <Input
                    id="nd-obs"
                    value={formLinea.observaciones}
                    onChange={(e) => setFormLinea({ ...formLinea, observaciones: e.target.value })}
                  />
                </div>

                {(editando?.advertencias || []).length > 0 && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <ul className="space-y-1">
                      {editando.advertencias.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Participación y costo por centro */}
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold">Participación en los centros</h4>
                    <p className="text-xs text-muted-foreground">
                      {formLinea.personalizada
                        ? 'Ajustada en esta nómina: al recalcular no se vuelve a traer la de la ficha.'
                        : 'Se está usando la participación de la ficha de la persona.'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormLinea((f) => ({ ...f, personalizada: !f.personalizada }))}
                  >
                    {formLinea.personalizada ? 'Volver a la de la ficha' : 'Ajustar solo en esta nómina'}
                  </Button>
                </div>

                {formLinea.personalizada && (
                  <ParticipacionCentros
                    metodo={formLinea.metodo}
                    centros={formLinea.centros}
                    sedes={sedes}
                    onChange={({ metodo, centros }) => setFormLinea((f) => ({ ...f, metodo, centros }))}
                    ayudaVacio="Elige al menos un centro para esta persona."
                  />
                )}

                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <Label className="text-xs">Costo por centro</Label>
                    <span className="text-xs text-muted-foreground">
                      {METODOS_DISTRIBUCION[editando?.metodoDistribucion] || ''} · último cálculo
                    </span>
                  </div>
                  <div className="rounded-md border [&>div]:max-h-[340px]">
                    <Table className="text-xs [&_td]:px-2 [&_td]:py-1.5 [&_th]:px-2 [&_th]:h-9">
                      <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-card">
                        <TableRow>
                          <TableHead>Centro</TableHead>
                          {editando?.metodoDistribucion === 'kilos' && <TableHead className="text-right">Kilos</TableHead>}
                          <TableHead className="text-right">Particip.</TableHead>
                          <TableHead className="text-right">Devengado</TableHead>
                          <TableHead className="text-right">Costo empresa</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(editando?.centros || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-muted-foreground">
                              Sin centros asignados: su costo no se carga a ninguna planta.
                            </TableCell>
                          </TableRow>
                        ) : (
                          editando.centros.map((c) => (
                            <TableRow key={c.id}>
                              <TableCell className="whitespace-nowrap">{c.nombre || c.id}</TableCell>
                              {editando.metodoDistribucion === 'kilos' && (
                                <TableCell className="text-right tabular-nums whitespace-nowrap">{kilos(c.kilos)}</TableCell>
                              )}
                              <TableCell className="text-right tabular-nums">{pct(c.factor)}</TableCell>
                              <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(c.devengado)}</TableCell>
                              <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                                {pesos(c.costoEmpresa)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                      {(editando?.centros || []).length > 0 && (
                        <TableFooter className="[&_td]:sticky [&_td]:bottom-0 [&_td]:z-10 [&_td]:bg-card">
                          <TableRow className="hover:bg-transparent">
                            <TableCell className="font-medium">Total</TableCell>
                            {editando.metodoDistribucion === 'kilos' && <TableCell />}
                            <TableCell className="text-right tabular-nums">100 %</TableCell>
                            <TableCell className="text-right tabular-nums whitespace-nowrap">
                              {pesos(editando.totalDevengado)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                              {pesos(editando.costoEmpresa?.total)}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Los montos se actualizan al guardar, que vuelve a calcular la nómina.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setEditando(null)} disabled={trabajando === 'linea'}>
              Cancelar
            </Button>
            <Button onClick={guardarLinea} disabled={trabajando === 'linea'}>
              {trabajando === 'linea' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando…
                </>
              ) : (
                'Guardar y recalcular'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* De dónde sale el costo de un centro: el aporte de cada persona */}
      <Dialog open={!!centroAbierto} onOpenChange={(v) => !v && setCentroAbierto(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle>{centroAbierto?.nombre || centroAbierto?.id}</DialogTitle>
            <DialogDescription>
              Cada persona reparte su costo con su propia participación; esto es la parte que le tocó a este
              centro en {nomina.nombre}.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat titulo="Personas" valor={centroAbierto?.personas ?? 0} />
              <Stat titulo="Kilos del periodo" valor={kilos(centroAbierto?.kilos)} />
              <Stat titulo="Participación" valor={pct(centroAbierto?.factor)} detalle="Del costo de esta nómina" />
              <Stat titulo="Costo empresa" valor={pesos(centroAbierto?.costoEmpresa)} destacado />
            </div>

            <div className="rounded-md border [&>div]:max-h-[360px]">
              <Table className="text-xs [&_td]:px-2 [&_td]:py-1.5 [&_th]:px-2 [&_th]:h-9">
                <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-card">
                  <TableRow>
                    <TableHead>Persona</TableHead>
                    <TableHead>Reparto</TableHead>
                    <TableHead className="text-right" title="Parte de la nómina de esa persona que carga a este centro">
                      Le carga
                    </TableHead>
                    <TableHead className="text-right">Devengado</TableHead>
                    <TableHead className="text-right">Neto</TableHead>
                    <TableHead className="text-right">Costo empresa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {centroAbierto &&
                    aportesDelCentro(centroAbierto.id).map(({ linea, parte }) => (
                      <TableRow key={linea._id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="font-medium">{linea.persona?.nombre}</div>
                          <div className="text-muted-foreground">{linea.persona?.cargo || 'Sin cargo'}</div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {METODOS_DISTRIBUCION[linea.metodoDistribucion] || linea.metodoDistribucion}
                          <div className="text-muted-foreground">
                            {(linea.centros || []).length} centro{(linea.centros || []).length === 1 ? '' : 's'}
                            {linea.metodoDistribucion === 'kilos' && ` · ${kilos(parte.kilos)}`}
                            {linea.metodoDistribucion === 'porcentaje' && parte.porcentaje != null && ` · ${parte.porcentaje} %`}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{pct(parte.factor)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(parte.devengado)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(parte.neto)}</TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                          {pesos(parte.costoEmpresa)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <TableFooter className="[&_td]:sticky [&_td]:bottom-0 [&_td]:z-10 [&_td]:bg-card">
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="font-medium" colSpan={3}>
                      Total del centro
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {pesos(centroAbierto?.devengado)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{pesos(centroAbierto?.neto)}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                      {pesos(centroAbierto?.costoEmpresa)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            <p className="text-xs text-muted-foreground">
              «Le carga» es la parte de la nómina de esa persona que va a este centro: con kilos sale de los que
              recibió la planta en el periodo, con porcentajes del que se le fijó, y equitativa reparte en partes
              iguales entre sus centros.
            </p>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setCentroAbierto(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular / eliminar */}
      <Dialog open={!!confirmar} onOpenChange={(v) => !v && setConfirmar(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmar === 'anular' ? 'Anular nómina directa' : 'Eliminar borrador'}</DialogTitle>
            <DialogDescription>
              {confirmar === 'anular'
                ? 'La nómina queda en el historial como anulada y deja de sumar al costo de los centros.'
                : 'Se borra la nómina y su reparto. Esta acción no se puede deshacer.'}
            </DialogDescription>
          </DialogHeader>
          {confirmar === 'anular' && (
            <div className="space-y-2">
              <Label htmlFor="nd-motivo">Motivo</Label>
              <Input
                id="nd-motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej. periodo mal definido"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={confirmar === 'anular' && !motivo.trim()}
              onClick={async () => {
                if (confirmar === 'anular') {
                  const ok = await accion(
                    'anular',
                    () => cambiarEstadoNominaDirecta(id, 'anular', { usuario, motivo }),
                    'Nómina anulada',
                  );
                  if (ok) setConfirmar(null);
                } else {
                  const ok = await accion('eliminar', () => eliminarNominaDirecta(id), 'Borrador eliminado');
                  if (ok) navigate('/personal/nomina-directa');
                }
              }}
            >
              {confirmar === 'anular' ? 'Anular' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default NominaDirectaDetallePage;
